import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Tooltip from "src/components/tooltip";
import styles from "../styles.module.scss";

/** Registry of close functions so only one dropdown is open at a time. */
const closeRegistry = new Set<() => void>();

function closeOtherDropdowns(except: () => void) {
  closeRegistry.forEach((close) => {
    if (close !== except) close();
  });
}

type Props = {
  user: {
    id?: string ;
    userGroups?: string[] | null;
  };
};

const MAX_VISIBLE_GROUPS = 1;
const MIN_SPACE_BELOW = 220;
const MIN_DROPDOWN_WIDTH = 280;
const SPACE_RIGHT_MARGIN = 16;

export default function GroupsList({
  user,
}: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [localOpen, setLocalOpen] = useState(false);
  const closeRef = useRef<() => void>(() => setLocalOpen(false));
  const [openAbove, setOpenAbove] = useState(false);
  const [openLeft, setOpenLeft] = useState(false);

  const isOpen = localOpen;

  const normalizedGroups = useMemo(() => {
    const raw = user?.userGroups;
    if (!Array.isArray(raw)) return [];

    return raw
      .filter((g): g is string => typeof g === "string" && g.trim() !== "")
      .map((label, index) => ({
        key: `${label}-${index}`,
        label,
      }));
  }, [user?.userGroups]);

  const close = useCallback(() => setLocalOpen(false), []);
  closeRef.current = close;

  useEffect(() => {
    closeRegistry.add(close);
    return () => {
      closeRegistry.delete(close);
    };
  }, [close]);

  const toggleOpen = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setLocalOpen((prev) => {
      if (!prev) closeOtherDropdowns(closeRef.current);
      return !prev;
    });
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setLocalOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !wrapRef.current) return;

    const rect = wrapRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    setOpenAbove(spaceBelow < MIN_SPACE_BELOW);

    const spaceRight = window.innerWidth - rect.right;
    setOpenLeft(spaceRight < MIN_DROPDOWN_WIDTH + SPACE_RIGHT_MARGIN);
  }, [isOpen]);

  if (!normalizedGroups.length) return null;

  const visibleGroups = normalizedGroups.slice(0, MAX_VISIBLE_GROUPS);
  const remainingGroups = normalizedGroups.slice(MAX_VISIBLE_GROUPS);
  const remainingCount = remainingGroups.length;

  return (
    <div
      className={styles.groupsList}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Visible groups */}
      {visibleGroups.map((group) => (
        <Tooltip key={group.key} content={group.label} placement="top">
          <span className={styles.groupPill}>
            {group.label}
          </span>
        </Tooltip>
      ))}

      {/* Dropdown */}
      {remainingCount > 0 && (
        <div ref={wrapRef} className={styles.groupsDropdownWrap}>
          <button
            type="button"
            className={styles.groupPillMore}
            onClick={toggleOpen}
          >
            +{remainingCount} more
          </button>

          {isOpen && (
            <div
              className={`${styles.groupsDropdownMenu} ${
                openAbove ? styles.groupsDropdownMenuAbove : ""
              } ${openLeft ? styles.groupsDropdownMenuLeft : ""}`}
            >
              <div className={styles.groupsOverflow}>
                <div className={styles.groupsOverflowTitle}>
                  List of user groups
                </div>

                {remainingGroups.map((group) => (
                  <div key={group.key} className={styles.groupsOverflowItem}>
                    {group.label}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
